
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Link as RouterLink } from "react-router-dom";

import { useStyle } from "../../context/styleContext/styleContext";
import Seo from "../../components/Seo";

import {
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import { CheckCircleRounded } from "@mui/icons-material";
import { Facebook, YouTube, X } from "@mui/icons-material";

//import SubscribeBanner from "../../components/SubscribeBanner/SubscribeBanner";
import HomePageProductCard from "../../components/ProductCard/HomePageProductCard";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
import HomeFeaturedProductCard from "../../components/ProductCard/HomeFeaturedProductCard";

export default function Home() {
  const { style } = useStyle();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [dataForAllProducts, setDataForAllProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);

  const topContainer = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  const getProductInfo = useCallback(async () => {
    try {
      setLoadingProducts(true);
      const { data } = await axios.post("/api/get-products-available-on-website");

      if (data) {
        setDataForAllProducts(data);
      } else {
        console.log("data error");
      }
    } catch (error) {
      console.log("Error retrieving data...", error);
    } finally {
      setLoadingProducts(false);
    }
  }, []);

  useEffect(() => {
    getProductInfo();
  }, [getProductInfo]);

  const featuredProduct = useMemo(() => {
    return dataForAllProducts[3] || null;
  }, [dataForAllProducts]);

  const newReleaseProducts = useMemo(() => {
    return dataForAllProducts.slice(0, 3);
  }, [dataForAllProducts]);

  return (
    <>
      <Seo
        title="Parish Audio | Professional Audio Plugins"
        description="Discover Parish Audio plugins for mixing, sound design, songwriting, and modern production. Instant downloads for Mac and Windows."
        canonical="https://parishaudio.com/"
      />

      <Box>
        <Box className="dark-alpha-80">
          <Paper
            ref={topContainer}
            sx={{
              position: "relative",
              overflow: "hidden",
              borderRadius: 3,
              mt: 0,
              width: "95%",
              maxWidth: "1000px",
              mx: "auto",
              minHeight: { xs: 460, sm: 500, md: 470, lg: 520 },
              display: "flex",
              alignItems: { xs: "stretch", md: "center" },
              backgroundColor: "black",
              backgroundImage: `
                linear-gradient(
                  90deg,
                  rgba(8, 12, 18, 0.92) 0%,
                  rgba(8, 12, 18, 0.82) 33%,
                  rgba(8, 12, 18, 0.58) 57%,
                  rgba(8, 12, 18, 0.30) 100%
                ),
                url('/assets/HeroBanner.webp')
              `,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
            }}
            elevation={6}
          >
            <Box
              sx={{
                width: "100%",
                p: { xs: 2, sm: 2.5, md: 3 },
                display: "flex",
                alignItems: { xs: "flex-end", md: "center" },
              }}
            >
              <Box sx={{ width: "100%", maxWidth: { xs: "100%", md: 580 } }}>
                <Stack spacing={{ xs: 1.6, md: 2.25 }} alignItems="flex-start">
                  <Chip
                    label="Professional Audio Plugins"
                    sx={{
                      bgcolor: "rgba(255,255,255,0.10)",
                      color: "white",
                      fontWeight: 700,
                      backdropFilter: "blur(6px)",
                    }}
                  />

                  <Typography
                    variant="h1"
                    sx={{
                      color: "white",
                      fontWeight: 800,
                      lineHeight: 1.02,
                      fontSize: {
                        xs: "2.35rem",
                        sm: "3rem",
                        md: "4rem",
                      },
                      textShadow: "0 4px 24px rgba(0,0,0,0.45)",
                    }}
                  >
                    Parish Audio
                  </Typography>

                  <Typography
                    sx={{
                      color: "rgba(255,255,255,0.92)",
                      fontWeight: 700,
                      lineHeight: 1.15,
                      fontSize: {
                        xs: "1.2rem",
                        sm: "1.45rem",
                        md: "2rem",
                      },
                      textShadow: "0 4px 24px rgba(0,0,0,0.4)",
                    }}
                  >
                    Powerful sound. Built for modern production.
                  </Typography>

                  <Typography
                    sx={{
                      color: "rgba(255,255,255,0.82)",
                      fontWeight: 400,
                      maxWidth: 540,
                      fontSize: {
                        xs: "0.98rem",
                        sm: "1.05rem",
                        md: "1.2rem",
                      },
                      textShadow: "0 3px 18px rgba(0,0,0,0.4)",
                    }}
                  >
                    Explore creative tools for mixing, sound design, songwriting,
                    and everyday creative sessions. Available on Mac and Windows.
                  </Typography>

                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={{ xs: 0.75, sm: 2.5 }}
                    sx={{
                      pt: 0.5,
                      flexWrap: "wrap",
                      width: "100%",
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CheckCircleRounded sx={{ color: "white" }} fontSize="small" />
                      <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.85)" }}>
                        Instant download
                      </Typography>
                    </Stack>

                    <Stack direction="row" spacing={1} alignItems="center">
                      <CheckCircleRounded sx={{ color: "white" }} fontSize="small" />
                      <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.85)" }}>
                        VST3 plugins
                      </Typography>
                    </Stack>

                    <Stack direction="row" spacing={1} alignItems="center">
                      <CheckCircleRounded sx={{ color: "white" }} fontSize="small" />
                      <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.85)" }}>
                        Mac + Windows
                      </Typography>
                    </Stack>
                  </Stack>

                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1.5}
                    sx={{
                      pt: { xs: 1.25, sm: 0.5 },
                      width: { xs: "100%", sm: "auto" },
                      mt: { xs: "auto", md: 0 },
                    }}
                  >
                    <Button
                      variant="contained"
                      size="large"
                      component={RouterLink}
                      to="/plugins"
                      fullWidth={isMobile}
                      sx={{
                        bgcolor: "white",
                        color: "primary.main",
                        fontWeight: 700,
                        minWidth: { sm: 170 },
                        "&:hover": {
                          bgcolor: "rgba(255,255,255,0.92)",
                        },
                      }}
                    >
                      Browse Plugins
                    </Button>

                    <Button
                      variant="outlined"
                      size="large"
                      component={RouterLink}
                      to="/products/d1-delay"
                      fullWidth={isMobile}
                      sx={{
                        borderColor: "rgba(255,255,255,0.40)",
                        color: "white",
                        minWidth: { sm: 170 },
                        "&:hover": {
                          borderColor: "rgba(255,255,255,0.65)",
                          bgcolor: "rgba(255,255,255,0.06)",
                        },
                      }}
                    >
                      View Featured
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            </Box>
          </Paper>

          <Box
            className="centered-main-container"
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "95%",
              maxWidth: "900px",
              mx: "auto",
              minHeight: "calc(100vh - 130px)",
            }}
          >
            <Box
              sx={{
                mt: 1,
                backgroundColor: style.color1,
                borderRadius: "8px",
                p: 0.25,
                contentVisibility: "auto",
                containIntrinsicSize: "600px",
              }}
            >
              <Typography
                component="h2"
                sx={{ textAlign: "left", mb: 0, ml: "30px", fontWeight: 700 }}
              >
                Featured:
              </Typography>

              {featuredProduct && (
                <Box
                  sx={{
                    minWidth: "100px",
                    backgroundColor: style.color1,
                    m: 1,
                    mt: 1,
                    filter: "brightness(1.05)",
                    boxShadow: 4,
                  }}
                >
                  <HomeFeaturedProductCard productData={featuredProduct} />
                </Box>
              )}
            </Box>

            <Box
              sx={{
                my: "25px",
                contentVisibility: "auto",
                containIntrinsicSize: "300px",
              }}
            >
              {/* <SubscribeBanner /> */}
            </Box>

            <Box
              sx={{
                mt: 2,
                backgroundColor: style.color1,
                borderRadius: "8px",
                p: 1,
                mb: 3,
                contentVisibility: "auto",
                containIntrinsicSize: "1200px",
              }}
            >
              <Typography
                component="h2"
                sx={{ textAlign: "left", mb: "20px", fontWeight: 700 }}
              >
                New Releases
              </Typography>

              {!isMobile && newReleaseProducts.length > 0 && (
                <Box sx={{ display: "flex", flexDirection: "row", p: 1 }}>
                  {newReleaseProducts.map((product, index) => (
                    <Box
                      key={product?._id || product?.productURI || index}
                      sx={{
                        flex: 1,
                        m: "10px",
                        boxShadow: 10,
                        filter: "brightness(1.05)",
                        minWidth: 0,
                      }}
                    >
                      <HomePageProductCard productData={product} />
                    </Box>
                  ))}
                </Box>
              )}

              {isMobile && (
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  {dataForAllProducts && dataForAllProducts.length > 0 ? (
                    dataForAllProducts.map((product, index) => (
                      <Box
                        key={product?._id || product?.productURI || index}
                        sx={{
                          backgroundColor: style.color1,
                          m: 0.5,
                          filter: "brightness(1.05)",
                          boxShadow: 10,
                          p: 1,
                        }}
                      >
                        <HomePageProductCard productData={product} />
                      </Box>
                    ))
                  ) : loadingProducts ? (
                    <Box>
                      <LoadingSpinner />
                    </Box>
                  ) : null}
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        <Box
  sx={{
    width: "95%",
    maxWidth: "800px",
    mx: "auto",
    textAlign: "center",
    mb: "25px",
  }}
>
  <Typography component="h4" sx={{ mb: "10px", fontWeight: 700 }}>
    Find us on:
  </Typography>

  <Stack
    direction="row"
    spacing={2.5}
    justifyContent="center"
    alignItems="center"
    sx={{ mt: 1 }}
  >
    <a
      href="https://facebook.com/parishaudio"
      aria-label="Visit Parish Audio on Facebook"
      style={{ display: "flex", alignItems: "center" }}
    >
      <Facebook
        sx={{
          fontSize: 34,
          color: "#1877F2",
          transition: "transform 0.2s ease, filter 0.2s ease",
          "&:hover": {
            transform: "scale(1.08)",
            filter: "brightness(1.08)",
          },
        }}
      />
    </a>

    <a
      href="https://x.com/Parish_Audio"
      aria-label="Visit Parish Audio on X"
      style={{ display: "flex", alignItems: "center" }}
    >
      <X
        sx={{
          fontSize: 30,
          color: style.color4,
          transition: "transform 0.2s ease, filter 0.2s ease",
          "&:hover": {
            transform: "scale(1.08)",
            filter: "brightness(1.08)",
          },
        }}
      />
    </a>

    <a
      href="https://youtube.com/@ParishAudio"
      aria-label="Visit Parish Audio on YouTube"
      style={{ display: "flex", alignItems: "center" }}
    >
      <YouTube
        sx={{
          fontSize: 40,
          color: "#FF0000",
          transition: "transform 0.2s ease, filter 0.2s ease",
          "&:hover": {
            transform: "scale(1.08)",
            filter: "brightness(1.08)",
          },
        }}
      />
    </a>
  </Stack>
</Box>
      </Box>
    </>
  );
}

// import {React, useState, useContext, useEffect, useRef} from "react"
// import {useStyle} from '../../context/styleContext/styleContext'

// import axios from 'axios'
// import Seo from '../../components/Seo';
// //import {toast} from 'react-hot-toast'

// import {
//   Box,
//   Button,
//   Card,
//   CardContent,
//   Chip,
//   Container,
//   Divider,
//   Grid,
//   Paper,
//   Rating,
//   Stack,
//   Typography
// } from '@mui/material';
// import {
//   AudioFileRounded,
//   CheckCircleRounded,
//   DownloadRounded,
//   FlashOnRounded
// } from '@mui/icons-material';
// import { InfinityReverb_0_0_1 } from "../../ProductInfo";
// import { Link as RouterLink } from 'react-router-dom';
// import ProductCard from "../../components/ProductCard/ProductCard";
// import SubscribeBanner from "../../components/SubscribeBanner/SubscribeBanner";
// import HomePageProductCard from "../../components/ProductCard/HomePageProductCard";
// import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
// import MobileProductCard from "../Plugins/MobileProductCard";
// import HomeFeaturedProductCard from "../../components/ProductCard/HomeFeaturedProductCard";


// export default function Home(){

//   const { style } = useStyle();
//   const [height, setHeight] = useState(window.innerHeight)
//   const [width, setWidth] = useState(window.setWidth)
//   const [dataForAllProducts, setDataForAllProducts] = useState([])

//   useEffect(() => {
//     function handleResize() {      
//       setWidth(window.innerWidth)
//       setHeight(window.innerHeight)
//   }
  
//     window.addEventListener('resize', handleResize)
//   })

  
// const isMobile = width <= 768;


// const topContainer = useRef();
//     useEffect(() => {
//         // To make sure page starts from the top
//         topContainer.current.scrollIntoView({ block: "end" });
//     }, []);



//     const getProductInfo = async (e) => {
//       try{
//         const {data} = await axios.post('/api/get-all-products')
//         if(data)
//         {
//            // console.log(data)
//            var result = [];
//            var count = 0;

//            for(var i in data){
//                count++;
//            }
//            setDataForAllProducts(data)
//         }
//         else{
//             console.log('data error')
//         }
//       }
//       catch
//       {
//         console.log('Error retrieving data...')
//       }
      
//   }
//   useEffect(() => {
//       getProductInfo();
//   }, []);



//     return(
//         <>
//         <Seo
//           title="Parish Audio | Professional Audio Plugins"
//           description="Discover Parish Audio plugins for mixing, sound design, songwriting, and modern production. Instant downloads for Mac and Windows."
//           canonical="https://parishaudio.com/"
//       />
//         <div>
//           <div className='dark-alpha-80'>
//           <Paper
//             ref={topContainer}
//             sx={{
//               position: 'relative',
//               overflow: 'hidden',
//               borderRadius: 3,
//               mt:0,
//               width:'95%',
//               maxWidth:'1000px',
//               margin:'auto',
//               minHeight: { xs: 380, sm: 430, md: 470, lg: 520 },
//               display: 'flex',
//               alignItems: { xs: 'flex-end', md: 'center' },
//               backgroundColor:'black',
//               backgroundImage: {
//                 xs: `
//                   linear-gradient(
//                     90deg,
//                     rgba(8, 12, 18, 0.92) 0%,
//                     rgba(8, 12, 18, 0.82) 33%,
//                     rgba(8, 12, 18, 0.48) 57%,
//                     rgba(8, 12, 18, 0.20) 100%
//                   ),
//                   url('/assets/HeroBanner.png')
//                 `,
//                 md: `
//                   linear-gradient(
//                     90deg,
//                     rgba(8, 12, 18, 0.92) 0%,
//                     rgba(8, 12, 18, 0.82) 33%,
//                     rgba(8, 12, 18, 0.48) 57%,
//                     rgba(8, 12, 18, 0.20) 100%
//                   ),
//                   url('/assets/HeroBanner.png')
//                 `
//               },
//               backgroundSize: 'cover',
//               backgroundPosition: 'center',
//               backgroundRepeat: 'no-repeat',
//             }}
//               >
//             <Box
//               sx={{
//                 width: '100%',
//                 p: { xs: 2, sm: 2.5, md: 3 },
//                 mt:'0px',
//                 mb:'0px',
//                 display: 'flex',
//                 alignItems: 'center',
//               }}
            
//             >
//               <Box sx={{ maxWidth: { xs: '100%', md: 580 } }}>
//                 <Stack spacing={{ xs: 1.75, md: 2.25 }} alignItems="flex-start">
//                   <Chip
//                     label="Professional Audio Plugins"
//                     sx={{
//                       bgcolor: 'rgba(255,255,255,0.10)',
//                       color: 'white',
//                       fontWeight: 700,
//                       backdropFilter: 'blur(6px)'
//                     }}
//                   />

//                   <Typography
//                     variant="h1"
//                     sx={{
//                       color: 'white',
//                       fontWeight: 800,
//                       lineHeight: 1.02,
//                       fontSize: {
//                         xs: '2.35rem',
//                         sm: '3rem',
//                         md: '4rem'
//                       },
//                       textShadow: '0 4px 24px rgba(0,0,0,0.45)'
//                     }}
//                   >
//                     Parish Audio
//                   </Typography>

//                   <Typography
//                     sx={{
//                       color: 'rgba(255,255,255,0.92)',
//                       fontWeight: 700,
//                       lineHeight: 1.15,
//                       fontSize: {
//                         xs: '1.2rem',
//                         sm: '1.45rem',
//                         md: '2rem'
//                       },
//                       textShadow: '0 4px 24px rgba(0,0,0,0.4)'
//                     }}
//                   >
//                     Powerful sound. Built for modern production.
//                   </Typography>

//                   <Typography
//                     sx={{
//                       color: 'rgba(255,255,255,0.82)',
//                       fontWeight: 400,
//                       maxWidth: 540,
//                       fontSize: {
//                         xs: '0.98rem',
//                         sm: '1.05rem',
//                         md: '1.2rem'
//                       },
//                       textShadow: '0 3px 18px rgba(0,0,0,0.4)'
//                     }}
//                   >
//                     Explore creative tools for mixing, sound design, songwriting,
//                     and everyday creative sessions. Available on Mac and Windows.
//                   </Typography>

//                   <Stack
//                     direction={{ xs: 'column', sm: 'row' }}
//                     spacing={1.5}
//                     sx={{ pt: 0.5, width: { xs: '100%', sm: 'auto' } }}
//                   >
//                     <Button
//                       variant="contained"
//                       size="large"
//                       component={RouterLink}
//                       to="/plugins"
//                       sx={{
//                         bgcolor: 'white',
//                         color: 'primary.main',
//                         fontWeight: 700,
//                         minWidth: { sm: 170 },
//                         '&:hover': {
//                           bgcolor: 'rgba(255,255,255,0.92)'
//                         }
//                       }}
//                     >
//                       Browse Plugins
//                     </Button>

//                     <Button
//                       variant="outlined"
//                       size="large"
//                       component={RouterLink}
//                       //to={featuredProduct ? `/products/${featuredProduct.productURI}` : '/products'}
//                       to={`/products/d1-delay`}
//                       sx={{
//                         borderColor: 'rgba(255,255,255,0.40)',
//                         color: 'white',
//                         minWidth: { sm: 170 },
//                         '&:hover': {
//                           borderColor: 'rgba(255,255,255,0.65)',
//                           bgcolor: 'rgba(255,255,255,0.06)'
//                         }
//                       }}
//                     >
//                       View Featured
//                     </Button>
//                   </Stack>

//                   <Stack
//                     direction={{ xs: 'column', sm: 'row' }}
//                     spacing={{ xs: 0.75, sm: 2.5 }}
//                     sx={{ pt: 0.5, flexWrap: 'wrap' }}
//                   >
//                     <Stack direction="row" spacing={1} alignItems="center">
//                       <CheckCircleRounded sx={{ color: 'white' }} fontSize="small" />
//                       <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)' }}>
//                         Instant download
//                       </Typography>
//                     </Stack>

//                     <Stack direction="row" spacing={1} alignItems="center">
//                       <CheckCircleRounded sx={{ color: 'white' }} fontSize="small" />
//                       <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)' }}>
//                         VST3 plugins
//                       </Typography>
//                     </Stack>

//                     <Stack direction="row" spacing={1} alignItems="center">
//                       <CheckCircleRounded sx={{ color: 'white' }} fontSize="small" />
//                       <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)' }}>
//                         Mac + Windows
//                       </Typography>
//                     </Stack>
//                   </Stack>
//                 </Stack>
//               </Box>
//             </Box>
//           </Paper>
//            <div className='centered-main-container' style={{display:'flex', flexDirection:'column', width:'95%', maxWidth:'900px', backgroundPosition: "center", minHeight:height - 130}}>


//            <Box sx={{mt:1, backgroundColor:style.color1, borderRadius:'8px', p:0.25}}>
//             <h2 style={{textAlign:'left', marginBottom:'0px', marginLeft:'30px'}}>
//              Featured:
//             </h2>
//           {dataForAllProducts[2] && (
//               <Box sx={{minWidth:'100px', backgroundColor:style.color1, m:1, mt:1, filter:'brightness(1.05)', boxShadow:4}}>

//                    <HomeFeaturedProductCard productData={dataForAllProducts[3]} />
//               </Box>
//         )
//       }
//           </Box>

//     <div style={{marginTop:'25px', marginBottom:'25px'}}>
//       <SubscribeBanner />
//     </div>

//           <Box sx={{mt:2, backgroundColor:style.color1, borderRadius:'8px', p:1, mb:3}}>
//             <h2 style={{textAlign:'left', marginBottom:'20px'}}>
//               New Releases
//             </h2>

//           {!isMobile && dataForAllProducts.length > 0 && (
// <>
//            <Box sx={{ display:'flex', flexDirection:'row', p:1}}>
//             <Box sx={{flex:1, margin:'10px', boxShadow:10, filter:'brightness(1.05)'}}>
//               <HomePageProductCard productData={dataForAllProducts[0]} />
//             </Box>
//             <Box sx={{flex:1, margin:'10px', boxShadow:10, filter:'brightness(1.05)'}}>
//               <HomePageProductCard productData={dataForAllProducts[1]} />
//             </Box>
//             <Box sx={{flex:1, margin:'10px', boxShadow:10, filter:'brightness(1.05)'}}>
//               <HomePageProductCard productData={dataForAllProducts[2]} />
//             </Box>


                
//        </Box>
// </>


//           )}


//       {isMobile && (
            
//             <Box sx={{ display:'flex', flexDirection:'column'}}>
//             {dataForAllProducts && dataForAllProducts.length > 0 ? (
  
//               dataForAllProducts.map((product, index) => (
//                 <Box sx={{backgroundColor:style.color1, m:0.5, filter:'brightness(1.05)', boxShadow:10,p:1}}>
//                      <HomePageProductCard key={index} productData={product} />
//                 </Box>
//           ))
//         ) : (
//           <Box>
//                   <LoadingSpinner />
//           </Box>
//         )}
//         </Box>
 
 
 
//            )}

//           </Box>
//           </div>
//           </div>
//           <div style={{width:'95%', maxWidth:'800px', margin:'auto', margin:'auto', textAlign:'center', marginBottom:'25px'}}>
//             <h4 style={{marginBottom:'10px'}}>Find us on:</h4> 
//             <a href="https://facebook.com/parishaudio" style={{paddingTop:'10px'}}><img src='FacebookIcon.png' alt="Facebook icon" style={{maxWidth:'33px', marginLeft:'12px'}} /></a>
//             <a href="https://x.com/Parish_Audio"><img src='XLogoWhite.png'  alt="X icon"style={{maxWidth:'29px', marginLeft:'15px'}} /></a>
//             <a href="https://youtube.com/@ParishAudio"><img src='YoutubeIcon.png' alt="Youtube icon" style={{maxWidth:'40px', marginLeft:'17px'}} /></a>
//             </div>
//         </div>
        
//         </>
//     )
// }

